import React, { useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  CloudDownload as CloudIcon,
  Image as ImageIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import axios from 'axios';
import './App.css';

function App() {
  const [keyword, setKeyword] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const cloudTypes = {
    baidu: { name: '百度网盘', color: '#2196F3' },
    aliyun: { name: '阿里云盘', color: '#FF9800' },
    quark: { name: '夸克网盘', color: '#9C27B0' },
    tianyi: { name: '天翼云盘', color: '#4CAF50' },
    uc: { name: 'UC网盘', color: '#FF5722' },
    mobile: { name: '移动云盘', color: '#607D8B' },
    '115': { name: '115网盘', color: '#795548' },
    pikpak: { name: 'PikPak', color: '#E91E63' },
    xunlei: { name: '迅雷网盘', color: '#3F51B5' },
    '123': { name: '123网盘', color: '#009688' },
    magnet: { name: '磁力链接', color: '#F44336' },
    ed2k: { name: 'ED2K链接', color: '#673AB7' }
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setError('请输入搜索关键词');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8888';
      const response = await axios.post(`${apiBaseUrl}/api/search`, {
        kw: keyword,
        res: 'merge'
      });
      
      // 处理API响应格式：{code, message, data: {total, merged_by_type}}
      if (response.data.code === 0) {
        setSearchResults(response.data.data);
      } else {
        setError(response.data.message || '搜索失败');
      }
    } catch (err) {
      setError('搜索失败，请检查网络连接或稍后重试');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // 可以添加一个提示
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  const getAvailableTypes = () => {
    if (!searchResults?.merged_by_type) return [];
    return Object.keys(searchResults.merged_by_type).filter(
      type => searchResults.merged_by_type[type].length > 0
    );
  };

  const getCurrentTypeData = () => {
    const availableTypes = getAvailableTypes();
    if (availableTypes.length === 0) return [];
    const currentType = availableTypes[activeTab] || availableTypes[0];
    return searchResults.merged_by_type[currentType] || [];
  };

  const getCurrentTypeName = () => {
    const availableTypes = getAvailableTypes();
    if (availableTypes.length === 0) return '';
    const currentType = availableTypes[activeTab] || availableTypes[0];
    return cloudTypes[currentType]?.name || currentType;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
          网盘资源搜索
        </Typography>
        <Typography variant="h6" align="center" sx={{ opacity: 0.9 }}>
          快速搜索各大网盘资源
        </Typography>
      </Paper>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="输入搜索关键词..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || !keyword.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{
              minWidth: 120,
              borderRadius: 2,
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)'
            }}
          >
            {loading ? '搜索中...' : '搜索'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {searchResults && (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              搜索结果
            </Typography>
            <Chip 
              label={`共找到 ${searchResults.total || 0} 个结果`} 
              color="primary" 
              variant="outlined"
            />
          </Box>

          {getAvailableTypes().length > 0 && (
            <>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
              >
                {getAvailableTypes().map((type, index) => {
                  const typeInfo = cloudTypes[type] || { name: type, color: '#666' };
                  const count = searchResults.merged_by_type[type]?.length || 0;
                  return (
                    <Tab
                      key={type}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: typeInfo.color
                            }}
                          />
                          {typeInfo.name}
                          <Chip size="small" label={count} />
                        </Box>
                      }
                      sx={{ textTransform: 'none' }}
                    />
                  );
                })}
              </Tabs>

              <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                {getCurrentTypeName()} ({getCurrentTypeData().length} 个资源)
              </Typography>

              <Grid container spacing={3}>
                {getCurrentTypeData().map((item, index) => (
                  <Grid item xs={12} md={6} lg={4} key={index}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', flex: 1 }}>
                            {item.note || '未命名资源'}
                          </Typography>
                          <CloudIcon color="primary" />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TimeIcon fontSize="small" />
                            {formatDateTime(item.datetime)}
                          </Typography>
                          
                          <Chip 
                            size="small" 
                            label={item.source || 'unknown'} 
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        </Box>

                        {item.images && item.images.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <ImageIcon fontSize="small" />
                              包含图片 ({item.images.length})
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'grey.50', 
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          wordBreak: 'break-all'
                        }}>
                          {item.url}
                          {item.password && (
                            <Box sx={{ mt: 1, color: 'primary.main' }}>
                              提取码: {item.password}
                            </Box>
                          )}
                        </Box>
                      </CardContent>

                      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                        <Button
                          size="small"
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="contained"
                          sx={{ borderRadius: 2 }}
                        >
                          访问链接
                        </Button>
                        
                        <Tooltip title="复制链接">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(item.password ? `${item.url} 提取码: ${item.password}` : item.url)}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {getAvailableTypes().length === 0 && (
            <Alert severity="info">
              没有找到相关资源，请尝试其他关键词
            </Alert>
          )}
        </Box>
      )}
    </Container>
  );
}

export default App;